/**
 * GhostMusician
 * V11 Feature 5: Intelligent Ghost Band Member
 * 
 * Creates an intelligent "second musician" that fills gaps,
 * adds countermelodies, and dynamically adjusts presence.
 * 
 * - Reactive Counterpoint: Responds with contrary motion to user melody
 * - Call and Response: Answers user phrases in silence gaps
 * - Bass Handoff: Adds walking bass when user plays high
 * - Dynamic Presence: Scales intensity with session energy
 */

import { midiToFreq } from '../../utils/math';

export type GhostEventType = 'countermelody' | 'pad' | 'walking_bass' | 'fill' | 'silent';

export interface GhostNote {
    freq: number;
    delay: number;
    velocity: number;
    duration: number;
}

export interface GhostEvent {
    notes: GhostNote[];
    type: GhostEventType;
}

export interface GhostContext {
    lastMelodyNote: number | null;
    lastMelodyDirection: 'up' | 'down' | 'neutral';
    lastBassNote: number | null;
    currentChord: number[];
    scaleNotes: number[];
    currentSection: 'Verse' | 'Chorus' | 'Bridge';
    sessionIntensity: number;       // 0-1 overall session energy
    userYPosition: number | null;   // 0-1 where user is playing (null if no recent touch)
    timeSinceLastTouch: number;     // ms since user last touched
    tuning: number;
}

export class GhostMusician {
    // Melody tracking for counterpoint
    private melodyHistory: number[] = [];
    private readonly MELODY_HISTORY_SIZE = 8;

    // Response timing
    private lastResponseTime: number = 0;
    private readonly RESPONSE_COOLDOWN_MS = 1500;

    // Presence level (ramps up/down smoothly)
    private presenceLevel: number = 0.3;
    private targetPresence: number = 0.3;

    constructor() {
        this.reset();
    }

    reset(): void {
        this.melodyHistory = [];
        this.lastResponseTime = 0;
        this.presenceLevel = 0.3;
        this.targetPresence = 0.3;
    }

    /**
     * Record a user melody note to build history for counterpoint.
     */
    recordUserMelody(midiNote: number): void {
        this.melodyHistory.push(midiNote);
        if (this.melodyHistory.length > this.MELODY_HISTORY_SIZE) {
            this.melodyHistory.shift();
        }
    }

    /**
     * Get the next ghost event based on context.
     * Returns notes to play or empty array for silence.
     */
    getGhostEvent(context: GhostContext): GhostEvent {
        // Update presence level smoothly
        this.updatePresence(context);

        // Decide what type of event to generate
        const eventType = this.decideEventType(context);

        if (eventType === 'silent') {
            return { notes: [], type: 'silent' };
        }

        const notes = this.generateNotes(eventType, context);

        return { notes, type: eventType };
    }

    /**
     * Update presence level based on session state.
     */
    private updatePresence(context: GhostContext): void {
        // Target presence based on intensity and section
        let target = context.sessionIntensity * 0.5;

        // Boost in Chorus, reduce in Verse
        switch (context.currentSection) {
            case 'Verse':
                target *= 0.6;
                break;
            case 'Chorus':
                target *= 1.3;
                break;
            case 'Bridge':
                target *= 0.9;
                break;
        }

        // Clamp target
        this.targetPresence = Math.max(0.1, Math.min(0.8, target));

        // Smooth transition
        const smoothing = 0.1;
        this.presenceLevel += (this.targetPresence - this.presenceLevel) * smoothing;
    }

    /**
     * Decide what type of ghost event to generate.
     */
    private decideEventType(context: GhostContext): GhostEventType {
        const now = Date.now();

        // During active playing, stay quiet
        if (context.timeSinceLastTouch < 500) {
            return 'silent';
        }

        // Cooldown between responses
        if (now - this.lastResponseTime < this.RESPONSE_COOLDOWN_MS) {
            return 'silent';
        }

        // Low presence = less active
        if (Math.random() > this.presenceLevel) {
            return 'silent';
        }

        // User playing high → ghost adds bass
        if (context.userYPosition !== null && context.userYPosition < 0.3) {
            this.lastResponseTime = now;
            return 'walking_bass';
        }

        // User playing low → ghost stays silent (bass handoff)
        if (context.userYPosition !== null && context.userYPosition > 0.7) {
            return 'silent';
        }

        // Extended silence → fill or countermelody
        if (context.timeSinceLastTouch > 2000) {
            this.lastResponseTime = now;
            // 60% countermelody, 40% fill
            return Math.random() < 0.6 ? 'countermelody' : 'fill';
        }

        // Moderate silence → subtle pad
        if (context.timeSinceLastTouch > 1000) {
            this.lastResponseTime = now;
            return 'pad';
        }

        return 'silent';
    }

    /**
     * Generate notes for the decided event type.
     */
    private generateNotes(type: GhostEventType, context: GhostContext): GhostNote[] {
        switch (type) {
            case 'countermelody':
                return this.generateCountermelody(context);
            case 'walking_bass':
                return this.generateWalkingBass(context);
            case 'pad':
                return this.generatePad(context);
            case 'fill':
                return this.generateFill(context);
            default:
                return [];
        }
    }

    /**
     * Generate countermelody that responds to user melody.
     * Uses contrary motion principle.
     */
    private generateCountermelody(context: GhostContext): GhostNote[] {
        if (context.scaleNotes.length === 0) return [];

        const notes: GhostNote[] = [];
        const velocity = this.presenceLevel * 0.4; // Soft, supportive

        // Determine contrary motion direction
        const direction = context.lastMelodyDirection === 'up' ? -1 :
            context.lastMelodyDirection === 'down' ? 1 : 0;

        // Start from a chord tone
        const startNote = context.currentChord.length > 0
            ? context.currentChord[Math.floor(Math.random() * context.currentChord.length)]
            : context.scaleNotes[0];

        // Generate 2-3 note response
        const noteCount = Math.random() < 0.5 ? 2 : 3;
        let currentNote = startNote;

        for (let i = 0; i < noteCount; i++) {
            notes.push({
                freq: midiToFreq(currentNote, context.tuning),
                delay: i * 0.3, // Spread out
                velocity: velocity * (1 - i * 0.1), // Slight fade
                duration: 0.4
            });

            // Move in contrary direction
            const scaleStep = direction * (Math.random() < 0.7 ? 2 : 3);
            const noteIndex = context.scaleNotes.indexOf(currentNote);
            if (noteIndex !== -1) {
                const newIndex = Math.max(0, Math.min(context.scaleNotes.length - 1, noteIndex + scaleStep));
                currentNote = context.scaleNotes[newIndex];
            }
        }

        return notes;
    }

    /**
     * Generate walking bass line when user plays high.
     */
    private generateWalkingBass(context: GhostContext): GhostNote[] {
        if (context.scaleNotes.length === 0) return [];

        const notes: GhostNote[] = [];
        const velocity = this.presenceLevel * 0.5;

        // Start from bass note or chord root
        const bassNote = context.lastBassNote !== null
            ? context.lastBassNote
            : context.currentChord.length > 0
                ? Math.min(...context.currentChord) - 12
                : context.scaleNotes[0] - 12;

        // Walking pattern: root, passing tone, target
        // Keep it simple: root and fifth
        notes.push({
            freq: midiToFreq(bassNote, context.tuning),
            delay: 0,
            velocity: velocity,
            duration: 1.0
        });

        // Add fifth after a beat
        const fifthNote = bassNote + 7;
        notes.push({
            freq: midiToFreq(fifthNote, context.tuning),
            delay: 0.8,
            velocity: velocity * 0.8,
            duration: 0.6
        });

        return notes;
    }

    /**
     * Generate subtle pad chord for background support.
     */
    private generatePad(context: GhostContext): GhostNote[] {
        if (context.currentChord.length === 0) return [];

        const notes: GhostNote[] = [];
        const velocity = this.presenceLevel * 0.25; // Very soft

        // Play current chord as pad (minus octave for warmth)
        context.currentChord.slice(0, 3).forEach((note, i) => {
            notes.push({
                freq: midiToFreq(note - 12, context.tuning),
                delay: i * 0.05, // Slight spread for warmth
                velocity: velocity,
                duration: 2.5 // Long sustain
            });
        });

        return notes;
    }

    /**
     * Generate a short melodic fill.
     */
    private generateFill(context: GhostContext): GhostNote[] {
        if (context.scaleNotes.length === 0) return [];

        const notes: GhostNote[] = [];
        const velocity = this.presenceLevel * 0.35;

        // Quick ascending or descending run
        const ascending = Math.random() < 0.5;
        const startIndex = ascending ? 2 : 5;
        const direction = ascending ? 1 : -1;

        for (let i = 0; i < 3; i++) {
            const noteIndex = Math.max(0, Math.min(context.scaleNotes.length - 1, startIndex + (i * direction)));
            notes.push({
                freq: midiToFreq(context.scaleNotes[noteIndex], context.tuning),
                delay: i * 0.12, // Quick succession
                velocity: velocity * (ascending ? 1 + i * 0.1 : 1 - i * 0.1),
                duration: 0.2
            });
        }

        return notes;
    }

    /**
     * Get the last user melody direction for call-and-response.
     */
    getMelodyDirection(): 'up' | 'down' | 'neutral' {
        if (this.melodyHistory.length < 2) return 'neutral';

        const recent = this.melodyHistory.slice(-3);
        let ups = 0, downs = 0;

        for (let i = 1; i < recent.length; i++) {
            if (recent[i] > recent[i - 1]) ups++;
            else if (recent[i] < recent[i - 1]) downs++;
        }

        if (ups > downs) return 'up';
        if (downs > ups) return 'down';
        return 'neutral';
    }

    /**
     * Get current presence level (0-1).
     */
    getPresenceLevel(): number {
        return this.presenceLevel;
    }
}
